import { useId } from "react";
import {
  VOICE_MODES,
  VOICE_MODE_LABELS,
  type AppelsCapabilities,
  type VoiceMode,
} from "@shared/modules/x-appels";
import { Card } from "../../../core/components/ui/card";

interface VoiceModeSelectorProps {
  value: VoiceMode;
  capabilities: AppelsCapabilities["voiceModes"];
  disabled: boolean;
  onChange: (mode: VoiceMode) => void;
}

export function VoiceModeSelector({
  value,
  capabilities,
  disabled,
  onChange,
}: VoiceModeSelectorProps) {
  const id = useId();
  if (capabilities.environment !== "preprod") return null;
  const selected = capabilities.options.find((option) => option.id === value);
  return (
    <Card
      title="Assistant de préproduction"
      subtitle="Choisissez l’assistant à essayer sur la ligne de test."
    >
      <fieldset className="voice-mode" aria-describedby={`${id}-help`} disabled={disabled}>
        <legend className="voice-mode__legend">Assistant téléphonique</legend>
        <div className="voice-mode__track" data-selected={value}>
          <span className="voice-mode__selection" aria-hidden="true" />
          {VOICE_MODES.map((mode) => {
            const available = capabilities.options.some(
              (option) => option.id === mode && option.available,
            );
            return (
              <label
                key={mode}
                className={`voice-mode__option${value === mode ? " is-selected" : ""}${available ? "" : " is-unavailable"}`}
              >
                <input
                  type="radio"
                  name={`${id}-mode`}
                  value={mode}
                  checked={value === mode}
                  disabled={!available}
                  onChange={() => onChange(mode)}
                  aria-describedby={`${id}-${mode}-status`}
                />
                <span className="voice-mode__label">{VOICE_MODE_LABELS[mode]}</span>
                <span className="voice-mode__status" id={`${id}-${mode}-status`}>
                  {available
                    ? mode === "current"
                      ? "Disponible"
                      : "Prêt à essayer"
                    : "À configurer"}
                </span>
              </label>
            );
          })}
        </div>
        <p className="repondeur__note" id={`${id}-help`}>
          Après enregistrement, le choix s’applique aux prochains appels. Un appel en cours conserve
          son assistant. Les services, les prix et l’agenda restent ceux de Protecto.
        </p>
        <p className="repondeur__note">
          OpenAI utilise sa propre voix. « Prêt à essayer » indique que le raccordement est
          configuré ; la qualité reste à comparer au téléphone.
        </p>
        {!capabilities.switchEnabled ? (
          <p className="repondeur__note" role="status">
            Les autres assistants ne sont pas encore activés pour les essais.
          </p>
        ) : selected && !selected.available ? (
          <p className="repondeur__note" role="status">
            L’assistant choisi nécessite une configuration. Vous pouvez revenir à l’assistant
            actuel.
          </p>
        ) : null}
      </fieldset>
    </Card>
  );
}
